// bluaIQ — TASK-WEB-1: view-model helpers for the read-only Takeoff display module.
//
// Two-view policy (per the accepted contract v2 planning artifact): BOTH views read the
// SAME validated payload; visibility is filtering ONLY, and that filtering happens here
// — in the data layer — never via CSS hide/show. The auditor view renders lines as-is;
// the client view is derived through deriveClientLine()/deriveClientLines() below, which
// strips designation/trust_tier/classification from every source and replaces the
// amount-redaction marker with a UI sentinel BEFORE the component ever sees it.

import type {
  BilingualLabel,
  QuantificationLine,
  QuantificationLineSource,
  QuantificationStatus,
} from '../../shared/websitePayloadContractV2'
import type {
  PlanQaIssueStatus,
  PlanQaReport,
  WebsiteTakeoffPayloadV3,
} from '../../shared/websitePayloadContractV3'

export type DisplayView = 'auditor' | 'client'

// The literal bilingual redaction marker the payload generator writes into `notes` in
// place of a real amount (per TASK-28: "real amount redactions occurred in baseline
// notes"). Matched defensively — case-insensitive, tolerant of internal whitespace.
const REDACTION_MARKER_RX = /\[\s*amount not shown in web view\s*\/\s*monto no mostrado en la vista web\s*\]/gi

/** Sentinel the UI recognizes and renders as the "Amount hidden" / "Monto oculto" badge.
 *  Never contains the raw marker wording, so a client-derived note can never leak it. */
export const REDACTED_AMOUNT_SENTINEL = ' REDACTED_AMOUNT '

export function noteContainsRedactionMarker(note: string): boolean {
  REDACTION_MARKER_RX.lastIndex = 0
  return REDACTION_MARKER_RX.test(note)
}

/** Split raw (auditor) note text around the marker, so the UI can render the segments
 *  before/after with the marker itself swapped for a full-text span or mobile badge. */
export function splitNoteAroundRedactionMarker(note: string): string[] {
  REDACTION_MARKER_RX.lastIndex = 0
  return note.split(REDACTION_MARKER_RX)
}

/** Split client-derived note text around the sentinel (never the raw marker text). */
export function splitNoteAroundSentinel(note: string): string[] {
  return note.split(REDACTED_AMOUNT_SENTINEL)
}

/** Client-safe source: evidence_class + ref + source_page only. No designation, no
 *  trust_tier, no classification — those are auditor-only provenance detail. */
export interface ClientQuantificationLineSource {
  evidence_class: string
  ref: string
  source_page?: string
}

export interface ClientQuantificationLine {
  section: string
  item_description: string
  quantity_value: number | null
  quantity_unit: string
  quantification_status: QuantificationStatus
  sources: ClientQuantificationLineSource[]
  notes: string
  packaging_only: true
  purchase_ready: false
  is_purchase_quantity: false
  /** Codex cielo-abovedado P0: the website must RECEIVE the line-blocking
   *  contract, not strip it. Both fields are structural and client-safe: the
   *  kind is an enum rendered ONLY through curated copy
   *  (clientBlockingKindCopy — null-not-slug), and the ids are the same
   *  question_id joins the Excel Preguntas sheet already prints. */
  client_blocking_kind?: 'plan_missing_information' | 'lane_not_available' | null
  client_question_ids?: string[]
  /** VAULTED-P1 facets, per-field projection rules (Codex order):
   *  · missing_input_request → CLIENT-SAFE SUBSET only: the bilingual
   *    `needed_input` prose and the plan-text evidence. The internal routing
   *    tokens (`kind`, `resolution_owner` — snake_case machine namespace) are
   *    dropped here; the auditor view reads the raw line and loses nothing.
   *  · diagnostic_projection → EXCLUDED ENTIRELY from the client model. It is
   *    an operand, not a quantity; the safest way to guarantee it is never
   *    presented as a measurement (Codex: "si dudas de cómo mostrarla, no la
   *    muestres y dilo") is for the client model not to carry it at all.
   *  · chained_to_line_ids → carried verbatim (structural ids). */
  missing_input_request?: ClientMissingInputRequest | null
  chained_to_line_ids?: string[] | null
  is_order_price_bearing: false
  /** Carried through from v4 payloads — the client view still needs MEP guide lines
   *  grouped apart from material quantities. Not provenance detail, so not stripped. */
  mep_guide_only?: true
}

// TASK-WEB-12 (Codex production blocker): metadata markers that must NEVER reach
// the client view. A source whose evidence_class or ref carries any of these is
// auditor-only provenance (owner designations, trust tiers, internal designation
// ids) and is dropped ENTIRELY from the client model — not just field-stripped,
// because the marker can live in the evidence_class/ref VALUES themselves
// (e.g. evidence_class "user_truth", ref "set1-w2-placed-count").
const CLIENT_EXCLUDED_SOURCE_RX = /user_truth|designation|trust_tier|classification/i

export function isClientSafeSource(source: QuantificationLineSource): boolean {
  return !CLIENT_EXCLUDED_SOURCE_RX.test(
    `${source.evidence_class} ${source.ref} ${source.source_page ?? ''}`,
  )
}

function deriveClientSource(source: QuantificationLineSource): ClientQuantificationLineSource {
  const out: ClientQuantificationLineSource = { evidence_class: source.evidence_class, ref: source.ref }
  if (source.source_page) out.source_page = source.source_page
  return out
}

/** Replace the marker with the sentinel — the client model never carries the raw
 *  bracket text, regardless of what the component later renders it as. */
function deriveClientNotes(notes: string): string {
  REDACTION_MARKER_RX.lastIndex = 0
  return notes.replace(REDACTION_MARKER_RX, REDACTED_AMOUNT_SENTINEL)
}

export interface ClientMissingInputRequest {
  needed_input: string
  evidence: { page: number; text_as_found: string }[]
}

function deriveClientMissingInput(
  req: QuantificationLine['missing_input_request'],
): ClientMissingInputRequest | null {
  if (!req) return null
  return {
    needed_input: req.needed_input,
    evidence: (Array.isArray(req.evidence) ? req.evidence : []).map((e) => ({
      page: e.page,
      text_as_found: e.text_as_found,
    })),
  }
}

export function deriveClientLine(line: QuantificationLine): ClientQuantificationLine {
  return {
    section: line.section,
    item_description: line.item_description,
    quantity_value: line.quantity_value,
    quantity_unit: line.quantity_unit,
    quantification_status: line.quantification_status,
    // TASK-WEB-12: metadata-bearing sources are excluded outright (see
    // isClientSafeSource); the survivors are then field-stripped as before.
    sources: line.sources.filter(isClientSafeSource).map(deriveClientSource),
    notes: deriveClientNotes(line.notes),
    packaging_only: true,
    purchase_ready: false,
    is_purchase_quantity: false,
    is_order_price_bearing: false,
    ...(line.mep_guide_only ? { mep_guide_only: true as const } : {}),
    // Line-blocking passthrough (Codex cielo-abovedado P0) — carried, never
    // rendered raw: the Shell routes the kind through clientBlockingKindCopy
    // and counts DISTINCT VALID question ids before speaking about them.
    ...(line.client_blocking_kind !== undefined ? { client_blocking_kind: line.client_blocking_kind } : {}),
    ...(Array.isArray(line.client_question_ids) ? { client_question_ids: [...line.client_question_ids] } : {}),
    // VAULTED-P1: client-safe subset of the missing-input request; the
    // diagnostic projection is DELIBERATELY not carried (operand, not a
    // quantity — see the type's projection-rules docstring).
    ...(line.missing_input_request ? { missing_input_request: deriveClientMissingInput(line.missing_input_request) } : {}),
    ...(Array.isArray(line.chained_to_line_ids) ? { chained_to_line_ids: [...line.chained_to_line_ids] } : {}),
  }
}

export function deriveClientLines(lines: QuantificationLine[]): ClientQuantificationLine[] {
  return lines.map(deriveClientLine)
}

/** TASK-WEB-36/37: the honesty counter shown above the result. Both counts are
 *  derived from the RAW candidate-only lines — they are view-independent FACTS
 *  (deriveClientLines is a 1:1 map, so the line set is identical in both views):
 *   - `blocked`: lines the engine could NOT measure — quantification_status
 *     `spec_present_quantity_pending` (the engine's blocked→pending mapping).
 *   - `measured`: lines that are NOT blocked AND carry a real numeric
 *     `quantity_value` — the plan gave us a number to stand behind.
 *
 *  TASK-37 fix (Codex strict-closeout blocker): the v6 engine composer emits
 *  BLOCKED lines with `quantity_value: 0` (NOT null) — a fixed contract
 *  (`clean_new_source_composer_area_only_rooms.py`: blocked = qty 0 + read_state
 *  "blocked" → website `spec_present_quantity_pending`). A `quantity_value !==
 *  null` test therefore counted every blocked line as ALSO measured, so
 *  measured double-counted the blocked lines (prod showed "42 con medida / 17
 *  bloqueadas" for a 42-line payload with 25 real + 17 blocked). Excluding the
 *  blocked status from `measured` makes the two counts DISJOINT — a line is
 *  either measured or blocked or neither, never both — so measured + blocked ≤
 *  total, always. Nothing is invented: a line with no numeric quantity (or a
 *  blocked line) is never counted as measured. */
export interface HonestyCounts {
  measured: number
  blocked: number
  total: number
}

/** D-25AGO-02 — the ONE structural predicate for "this line carries a
 *  measurement we are allowed to DISPLAY as a number".
 *
 *  THE DEFECT IT CLOSES: the engine composer emits BLOCKED lines as
 *  `quantity_value: 0` + `spec_present_quantity_pending` — the fixed contract
 *  documented on deriveHonestyCounts above. Every quantity slot that printed the
 *  RAW value therefore rendered a PLACEHOLDER as a measurement: "0 SF" on a line
 *  the engine never measured. That is not a formatting nit, it is a false
 *  measurement claim, and it is the dominant shape in real payloads — 19 such
 *  lines in Spring Garden, 7 in C-1600, 8 in the C-1500 demo payload.
 *
 *  STRUCTURAL ONLY — status and value, nothing else. Never prose: not `notes`,
 *  not `item_description`, not the word "pending". And never
 *  `client_blocking_kind` on its own — a line can be blocked pending a client
 *  QUESTION and still carry a real measurement, which stays visible.
 *
 *  Note what this deliberately is NOT: `quantity_value === 0 → hide`. A TRUE
 *  measured zero (a real status with a real zero) is a fact the plan supports,
 *  and it keeps rendering as `0` + its unit.
 *
 *  This is the SAME rule `deriveHonestyCounts` uses for `measured` and the Excel
 *  exporter uses for `lineIsMeasured` — all three call THIS function, so the
 *  honesty counter, the page and the workbook cannot drift into three answers. */
export function lineHasDisplayableMeasurement(line: {
  quantity_value: number | null | undefined
  quantification_status: QuantificationStatus
}): boolean {
  if (line.quantification_status === 'spec_present_quantity_pending') return false
  return line.quantity_value !== null && line.quantity_value !== undefined
}

/** The number a quantity slot may print, or `null` when this line has no
 *  displayable measurement. Callers render `null` through their EXISTING
 *  unavailable presentation (`—`); this never invents a substitute value and
 *  never mutates the persisted payload. */
export function displayableQuantity(line: {
  quantity_value: number | null | undefined
  quantification_status: QuantificationStatus
}): number | null {
  return lineHasDisplayableMeasurement(line) ? (line.quantity_value as number) : null
}

export function deriveHonestyCounts(lines: QuantificationLine[]): HonestyCounts {
  let measured = 0
  let blocked = 0
  for (const line of lines) {
    const isBlocked = line.quantification_status === 'spec_present_quantity_pending'
    if (isBlocked) {
      blocked += 1
      continue // blocked lines are NEVER counted as measured (even with qty 0)
    }
    // Same predicate the display surfaces use — one semantic, three callers.
    if (lineHasDisplayableMeasurement(line)) measured += 1
  }
  return { measured, blocked, total: lines.length }
}

// TASK-WEB-38: group the BLOCKED lines by WHY they're blocked. The v6 generic
// path stamps each blocked line's note with `BLOCKED:<slug>` (engine
// generic_quantification_from_probe.py BLOCK_REASONS); the slug maps to a
// builder-facing bucket. A blocked line with no recognizable slug (older
// non-v6 blocked notes: MEP proposals, trim-registry notes) is never dropped —
// it falls into an honest `other` bucket. View-independent facts.
//
// TASK-WEB-40 (Codex finding, C-1600 exam): the whole-trade lines blocked for
// wall/elevation geometry — drywall, paint, roofing, siding, insulation,
// framing — are NOT rooms, and grouping them under "rooms without dimensions"
// mis-described them. They now get their own bucket, named after the engine's
// OWN reason text for the slug ("requiere geometría de muros/alzados …" /
// "requires wall/elevation geometry …"), so the bucket says what the engine
// says. This is the dominant real-payload case, not an edge case: in BOTH real
// production payloads it is 6 of the blocked lines (C-1600 6/9, Spring Garden
// 6/19), so before this fix the rooms bucket absorbed every blocked line in
// both projects and was the only bucket shown.
export type BlockedGroupKey =
  | 'rooms_without_dimensions'
  | 'requires_wall_or_elevation_geometry'
  | 'pending_schedule_rows'
  | 'excluded_candidates'
  | 'other'

// Every key is a real slug from the engine's BLOCK_REASONS (all 8 mapped).
const BLOCK_SLUG_TO_GROUP: Record<string, BlockedGroupKey> = {
  room_perimeter_not_printed: 'rooms_without_dimensions',
  room_label_without_printed_dimensions: 'rooms_without_dimensions',
  no_room_records_with_printed_dimensions: 'rooms_without_dimensions',
  requires_wall_or_elevation_geometry_not_in_first_read_probe: 'requires_wall_or_elevation_geometry',
  schedule_qty_not_machine_readable: 'pending_schedule_rows',
  columnar_schedule_quantity_not_safe: 'pending_schedule_rows',
  dimension_association_suspect: 'excluded_candidates',
  room_label_ambiguous_provenance: 'excluded_candidates',
}

// Fixed display order — buckets render in this sequence, empty ones omitted.
const BLOCKED_GROUP_ORDER: BlockedGroupKey[] = [
  'rooms_without_dimensions',
  'requires_wall_or_elevation_geometry',
  'pending_schedule_rows',
  'excluded_candidates',
  'other',
]

const BLOCKED_SLUG_RX = /^BLOCKED:([a-z0-9_]+)\b/i

/** Map one blocked line to its group via the `BLOCKED:<slug>` note marker. */
export function blockedGroupOf(line: { notes: string }): BlockedGroupKey {
  const m = BLOCKED_SLUG_RX.exec(line.notes.trim())
  const slug = m ? m[1] : ''
  return BLOCK_SLUG_TO_GROUP[slug] ?? 'other'
}

export interface BlockedGroup {
  key: BlockedGroupKey
  count: number
  lines: QuantificationLine[]
}

/** Group the blocked (`spec_present_quantity_pending`) candidate lines by reason
 *  bucket. Returns only NON-EMPTY buckets in the fixed display order. Every
 *  blocked line lands in exactly one bucket (no silent drop); the counts sum to
 *  the blocked total from deriveHonestyCounts. */
export function deriveBlockedGroups(lines: QuantificationLine[]): BlockedGroup[] {
  const buckets = new Map<BlockedGroupKey, QuantificationLine[]>()
  for (const line of lines) {
    if (line.quantification_status !== 'spec_present_quantity_pending') continue
    const key = blockedGroupOf(line)
    const arr = buckets.get(key) ?? []
    arr.push(line)
    buckets.set(key, arr)
  }
  return BLOCKED_GROUP_ORDER
    .filter((k) => buckets.has(k))
    .map((k) => ({ key: k, count: buckets.get(k)!.length, lines: buckets.get(k)! }))
}

/** Only `quantity_confirmed_from_plan` is a "done" state. The other three are grouped
 *  as "in progress" in the client view — never rendered with error/alarm styling. */
export function isPendingStatus(status: QuantificationStatus): boolean {
  return status !== 'quantity_confirmed_from_plan'
}

// Fallback bilingual status labels — used only when payload.labels does not carry an
// override for the given status key (per requirement: "usa las del payload.labels
// cuando existan"). EN wording for #2-#4 is a direct translation of the ES source text
// supplied for this task; that ES text is authoritative for those three keys.
const FALLBACK_STATUS_LABELS: Record<QuantificationStatus, BilingualLabel> = {
  quantity_confirmed_from_plan: {
    en: 'Confirmed from the plans — not purchase-ready',
    es: 'Confirmado desde los planos — no listo para compra',
  },
  quantity_reading_in_progress: {
    en: 'Additional automated reading in progress',
    es: 'Lectura automática adicional en proceso',
  },
  quantity_estimated_from_user_budget: {
    en: 'Estimated from your budget — confirm before buying',
    es: 'Estimado según su presupuesto — confirmar antes de comprar',
  },
  spec_present_quantity_pending: {
    en: 'Specification identified — measurement pending',
    es: 'Especificación identificada — medición pendiente',
  },
}

function isBilingualLabel(value: unknown): value is BilingualLabel {
  return !!value && typeof value === 'object'
    && typeof (value as BilingualLabel).en === 'string' && !!(value as BilingualLabel).en
    && typeof (value as BilingualLabel).es === 'string' && !!(value as BilingualLabel).es
}

/** Resolve a status's display label: prefer payload.labels[status] when it is a valid
 *  bilingual pair, otherwise fall back to the baked-in copy above. */
export function resolveStatusLabel(
  status: QuantificationStatus,
  payloadLabels: Record<string, unknown> | undefined,
): BilingualLabel {
  const fromPayload = payloadLabels?.[status]
  return isBilingualLabel(fromPayload) ? fromPayload : FALLBACK_STATUS_LABELS[status]
}

export function pickLabel(label: BilingualLabel, isEs: boolean): string {
  return isEs ? label.es : label.en
}

// ─── v4 payload shapes (TASK-WEB-4) ─────────────────────────────────────────
//
// The v4 package introduced three note-borne shapes the UI renders structurally:
// coverage BANDS inside notes (point measurement + honest range), one exterior
// WATERFALL line (gross − exact deductions = net, doors NOT applied), and MEP
// guide/proposal sections ("MEP — …") that must NEVER sit next to material
// quantities. All three are parsed here — in the data layer — so both views render
// from the same derived structures.

/** One coverage band found inside a note, e.g. "585.0–730.0 lf" → {585, 730, 'lf'}. */
export interface CoverageBand {
  min: number
  max: number
  unit: string
}

// A band is "<number>–<number> <material unit>". Unit list is deliberately narrow —
// material units only — so indicative MEP ranges like "2.0–3.0 ton" never render as a
// material coverage band.
const COVERAGE_BAND_RX = /(\d+(?:\.\d+)?)\s*[–—-]\s*(\d+(?:\.\d+)?)\s*(lf|sf|pzas|pcs|piezas|each|ea)\b/gi

export function parseCoverageBands(note: string): CoverageBand[] {
  COVERAGE_BAND_RX.lastIndex = 0
  const out: CoverageBand[] = []
  let m: RegExpExecArray | null
  while ((m = COVERAGE_BAND_RX.exec(note)) !== null) {
    out.push({ min: Number(m[1]), max: Number(m[2]), unit: m[3].toLowerCase() })
  }
  return out
}

/** Compact chip text for a band: integers, en dash — "585–730 lf". */
export function formatCoverageBand(band: CoverageBand): string {
  return `${Math.round(band.min)}–${Math.round(band.max)} ${band.unit}`
}

/** Structured reading of a v4 exterior waterfall note. */
export interface WaterfallBreakdown {
  gross: string
  deductions: string
  net: string
  doorsNotApplied: boolean
  selectionPending: boolean
  /** Material alternatives WITHOUT prices — the note never carries prices (contract). */
  alternatives: string[]
}

// "gross 1980 sf − exact window openings 230 sf = net 1750 sf"
const WATERFALL_RX = /gross\s+(\d[\d,.]*\s*[a-z]+)\s*[−–-]\s*(.+?\d[\d,.]*\s*[a-z]+)\s*=\s*net\s+(\d[\d,.]*\s*[a-z]+)/i
const ALTERNATIVES_RX = /alternatives?\s*(?:\([^)]*\))?\s*:\s*([^.]+)/i

export function parseWaterfallNote(note: string): WaterfallBreakdown | null {
  const m = WATERFALL_RX.exec(note)
  if (!m) return null
  const altMatch = ALTERNATIVES_RX.exec(note)
  const alternatives = altMatch
    ? altMatch[1].split('|').map((s) => s.trim()).filter(Boolean)
    : []
  return {
    gross: m[1].trim(),
    deductions: m[2].trim(),
    net: m[3].trim(),
    doorsNotApplied: /doors\s+NOT\s+applied|puertas\s+NO\s+aplicadas/i.test(note),
    selectionPending: /selection\s+pending|selecci[oó]n(?:\s+de\s+material)?\s+pendiente/i.test(note),
    alternatives,
  }
}

/** v4 MEP guide sections are named "MEP — …". They group under a separate
 *  "Guides & proposals" block — never next to material quantities (Codex judgment). */
export const MEP_SECTION_PREFIX = 'MEP — '

export function isMepGuideSection(section: string): boolean {
  return section.startsWith(MEP_SECTION_PREFIX)
}

/** Partition already-grouped sections into material vs MEP-guide blocks. Order within
 *  each block is preserved. Done here — in the view model — so the visual separation
 *  is a data-layer grouping, not a CSS arrangement. */
export function partitionMepSections<T extends { section: string }>(
  sections: [string, T[]][],
): { material: [string, T[]][]; mep: [string, T[]][] } {
  const material: [string, T[]][] = []
  const mep: [string, T[]][] = []
  for (const entry of sections) {
    if (isMepGuideSection(entry[0])) mep.push(entry)
    else material.push(entry)
  }
  return { material, mep }
}

// ─── TASK-WEB-6: Plan QA partition + mobile pending highlights ───────────────

/** Data-layer partition of the Plan QA report: Plan QA is a SEPARATE capability
 *  from preliminary quantification (Codex rule) and must never be mixed into the
 *  quantity blocks. The report is extracted here — not filtered in CSS. */
export function getPlanQaReport(payload: WebsiteTakeoffPayloadV3): PlanQaReport | null {
  return payload.plan_qa_report ?? null
}

/** TASK-WEB-12 (Codex production blocker): the CLIENT Plan QA view never carries
 *  designations_applied — those are the owner's internal designation ids
 *  (auditor-only provenance). Derived in the data layer, never hidden via CSS. */
export function deriveClientPlanQa(report: PlanQaReport): PlanQaReport {
  return { ...report, designations_applied: [] }
}

/** Bilingual labels for the 5 contract-v3 Plan QA issue statuses. Positive framing:
 *  found and corrected inconsistencies are value delivered, never a system failure —
 *  and never a blanket plan approval. */
export const PLAN_QA_STATUS_LABELS: Record<PlanQaIssueStatus, BilingualLabel> = {
  resolved_by_plan_revision: {
    en: 'Corrected in the plan revision',
    es: 'Corregido en la revisión del plano',
  },
  resolved_by_user_designation: {
    en: 'Resolved by your designation',
    es: 'Resuelto por su designación',
  },
  label_absent_in_rev2: {
    en: 'Label still absent in revision 2',
    es: 'Etiqueta aún ausente en la revisión 2',
  },
  label_error_confirmed_resolution_pending_corrected_dimension: {
    en: 'Label error confirmed — corrected dimension pending',
    es: 'Error de etiqueta confirmado — dimensión corregida pendiente',
  },
  pending: {
    en: 'Pending confirmation',
    es: 'Pendiente de confirmación',
  },
}

// ── TASK-WEB-9: bilingual splitter for engine system texts ─────────────────────
//
// The engine emits SYSTEM texts as COMBINED BILINGUAL strings joined by " / "
// (order varies: real notes ship EN-first, safe_next_action ships ES-first). The
// UI must show ONLY the active language. This splitter is deliberately SAFE:
// it only splits when a " / " candidate separates two substantial halves that
// DETECT as different languages. Anything else — technical fragments, unit
// slashes like "585.0–730.0 lf / 54–68 pzas", raw evidence, single-language
// notes — is returned AS-IS with isOriginal=true, and the caller must render the
// "Texto original del plano / Original plan text" chip (Codex rule: raw evidence
// is labeled, never translated at runtime).

export interface SplitBilingualResult {
  text: string
  /** true when no clear bilingual separator exists — render the original text
   *  plus the original-plan-text chip. */
  isOriginal: boolean
}

// Cheap per-half language scoring: accented characters weigh as Spanish; common
// stopwords weigh per language. A half "is Spanish" when its Spanish hits beat
// its English hits. A valid bilingual pair needs the two halves to DIFFER.
const ES_HINT_RX = /[áéíóúñü¿¡]|\b(el|la|los|las|de|del|al|en|con|para|por|según|sus|una|un|es|está|aquí|nada|desde|aún|solo|sin|y|o|no|ya|cuarto|cuartos|etiqueta|etiquetas|línea|líneas|revisión|cuadro|pendiente|pendientes|proceso|propietaria|cantidad|conteo|marcas|coinciden|colocadas|plano|planos)\b/gi
const EN_HINT_RX = /\b(the|of|from|with|and|for|is|are|was|not|no|here|this|that|their|your|owner|room|rooms|label|labels|schedule|pending|progress|before|after|purchase|order|lines|plan|plans|count|marks|revision)\b/gi

function countMatches(text: string, rx: RegExp): number {
  rx.lastIndex = 0
  let n = 0
  while (rx.exec(text) !== null) n += 1
  return n
}

function isSpanishish(text: string): boolean {
  return countMatches(text, ES_HINT_RX) > countMatches(text, EN_HINT_RX)
}

/** Mask redaction markers so their INTERNAL " / " (the marker itself is
 *  bilingual) is never mistaken for the top-level separator. Same length, so
 *  candidate indices stay aligned with the original string. */
function maskRedactionMarkersForSplit(text: string): string {
  REDACTION_MARKER_RX.lastIndex = 0
  return text.replace(REDACTION_MARKER_RX, (m) => '#'.repeat(m.length))
}

const UNIT_TAIL_RX = /\d[\d.,–-]*\s*(lf|sf|pzas|pcs|piezas|each|ea|ton|ft|in)\s*$/i

export function splitBilingual(text: string, lang: 'es' | 'en'): SplitBilingualResult {
  const masked = maskRedactionMarkersForSplit(text)
  const rx = / \/ /g
  let m: RegExpExecArray | null
  while ((m = rx.exec(masked)) !== null) {
    const left = text.slice(0, m.index).trim()
    const right = text.slice(m.index + 3).trim()
    // Both halves must be substantial sentence-like text…
    if (left.length < 12 || right.length < 12) continue
    // …never a unit fragment ("585.0–730.0 lf / 54–68 pzas")…
    if (UNIT_TAIL_RX.test(left) || /^[\d(–-]/.test(right)) continue
    // …and must detect as DIFFERENT languages (order-agnostic: the real engine
    // ships notes EN-first but safe_next_action ES-first).
    const leftEs = isSpanishish(left)
    const rightEs = isSpanishish(right)
    if (leftEs === rightEs) continue
    const es = leftEs ? left : right
    const en = leftEs ? right : left
    return { text: lang === 'es' ? es : en, isOriginal: false }
  }
  return { text, isOriginal: true }
}

// TASK-WEB-39: the v7 perimeter composer joins SEVERAL bilingual segments into
// one note with "; " — `<ES1> / <EN1>; <ES2> / <EN2>; …` (real prod shape, e.g.
// "perímetro candidato, pendiente de revisión / candidate perimeter, pending
// review; la compuerta de área prueba consistencia, no identidad / the area gate
// proves consistency, not identity; …"). splitBilingual above picks ONE " / " and
// returns one half of the WHOLE string, so whichever side it lands on strands the
// other language's earlier segments — in prod that dropped BOTH English warnings
// from the BATHROOM 205 baseboard line. Splitting per segment fixes it: each
// segment is resolved independently and the active-language halves are rejoined.
//
// Segments are cut at "; " at paren depth 0 only, because a segment's own halves
// contain "; " internally ("perímetro bruto 2×(W+D); vanos de puerta sin
// descontar (…) / gross perimeter 2×(W+D); door openings not deducted (…)") —
// those inner fragments are single-language and are placed by language score.
// A fragment that scores NEUTRAL is kept in BOTH languages: never dropped.
function splitNoteSegments(text: string): string[] {
  const masked = maskRedactionMarkersForSplit(text)
  const out: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < masked.length; i += 1) {
    const c = masked[i]
    if (c === '(') depth += 1
    else if (c === ')') depth = Math.max(0, depth - 1)
    else if (c === ';' && depth === 0 && masked[i + 1] === ' ') {
      out.push(text.slice(start, i))
      start = i + 2
      i += 1
    }
  }
  out.push(text.slice(start))
  return out.map((s) => s.trim()).filter((s) => s.length > 0)
}

/** Active-language text of a MULTI-SEGMENT bilingual note (v7 composer shape).
 *  Single-segment notes fall through to splitBilingual unchanged, so every
 *  pre-v7 note keeps its exact current behavior (including the original-text
 *  chip). Display only — parsers and the auditor always read the RAW note. */
export function splitBilingualNote(text: string, lang: 'es' | 'en'): SplitBilingualResult {
  const segments = splitNoteSegments(text)
  if (segments.length <= 1) return splitBilingual(text, lang)
  const picked: string[] = []
  let sawBilingualPair = false
  for (const seg of segments) {
    const r = splitBilingual(seg, lang)
    if (!r.isOriginal) {
      sawBilingualPair = true
      picked.push(r.text)
      continue
    }
    const es = countMatches(seg, ES_HINT_RX)
    const en = countMatches(seg, EN_HINT_RX)
    // Neutral fragments (es === en) are kept in BOTH languages — an unreadable
    // fragment is shown, never silently dropped.
    if (es === en || (es > en) === (lang === 'es')) picked.push(seg)
  }
  // No segment was a real bilingual pair → this is not a combined note; keep
  // today's behavior (original text + chip) rather than reflowing it.
  if (!sawBilingualPair) return splitBilingual(text, lang)
  return { text: picked.join('; '), isOriginal: false }
}

// TASK-WEB-39 (Codex rule: the client may simplify LANGUAGE but must NEVER hide
// the ambiguity's values). The v7 composer lists an ambiguous room's candidate
// perimeters ONCE, physically inside the SPANISH half of its segment:
// "…; opciones listadas: 37.67 LF / 38.33 LF — la ambigüedad jamás se resuelve
// en silencio / options listed — ambiguity is never resolved silently; …".
// The English half carries NO values, so no language split can ever surface them
// on that side. The values are therefore parsed out of the RAW note here and
// rendered as their own element next to the blocked line, in BOTH languages and
// BOTH views. Values are numeric + unit — identical in either language, never
// translated, never reordered, never resolved to a single winner.
const AMBIGUOUS_OPTIONS_RX = /opciones listadas:\s*([^—]+?)\s*—/i

export function deriveAmbiguousOptions(note: string | null | undefined): string[] {
  const m = AMBIGUOUS_OPTIONS_RX.exec(note ?? '')
  if (!m) return []
  return m[1]
    .split(' / ')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

// TASK-WEB-38: the v6 area composer emits NESTED bilingual titles —
// `<ES prose> (área impresa del plano, preliminar / printed area from the plan,
// preliminary) / <EN prose>` (generic_quantification_from_probe.py). The plain
// splitBilingual above lands on the FIRST " / " (inside the parens) and garbles
// them, so line titles need a paren-aware split.
const PAREN_BILINGUAL_RX = /\(([^()]*\s\/\s[^()]*)\)/g

/** Depth of unclosed "(" immediately before `index` (0 = top level). */
function parenDepthBefore(text: string, index: number): number {
  let depth = 0
  for (let i = 0; i < index; i += 1) {
    if (text[i] === '(') depth += 1
    else if (text[i] === ')' && depth > 0) depth -= 1
  }
  return depth
}

/** Strip "(…)" groups so an inner paren's OPPOSITE-language copy can never
 *  pollute the language score of the side it sits in. */
function stripParens(text: string): string {
  return text.replace(/\([^()]*\)/g, ' ')
}

/** Collapse an inner bilingual parenthetical `(X / Y)` to the active language:
 *  `(área impresa… / printed area…)` → `(área impresa…)` for ES. Only collapses
 *  when the paren content is a genuine bilingual pair (splitBilingual agrees). */
function collapseInnerBilingualParens(text: string, lang: 'es' | 'en'): string {
  PAREN_BILINGUAL_RX.lastIndex = 0
  return text.replace(PAREN_BILINGUAL_RX, (whole, inner: string) => {
    const r = splitBilingual(inner, lang)
    return r.isOriginal ? whole : `(${r.text})`
  })
}

/** Active-language client-view line title (TASK-WEB-38). Splits the OUTERMOST
 *  (paren-depth-0) bilingual `ES / EN` boundary, scoring each side with its
 *  parens stripped, then collapses the surviving side's inner bilingual
 *  parenthetical to the active language. A single-language title (no top-level
 *  bilingual boundary) is returned unchanged. Presentation only; the auditor
 *  view keeps the raw item_description, and product specs are never
 *  runtime-translated (only the language the plan already printed is chosen). */
export function splitBilingualTitle(text: string, lang: 'es' | 'en'): SplitBilingualResult {
  const masked = maskRedactionMarkersForSplit(text)
  const rx = / \/ /g
  let m: RegExpExecArray | null
  while ((m = rx.exec(masked)) !== null) {
    if (parenDepthBefore(masked, m.index) > 0) continue // skip inner-paren separators
    const left = text.slice(0, m.index).trim()
    const right = text.slice(m.index + 3).trim()
    if (left.length < 12 || right.length < 12) continue
    if (UNIT_TAIL_RX.test(left) || /^[\d(–-]/.test(right)) continue
    const leftEs = isSpanishish(stripParens(left))
    const rightEs = isSpanishish(stripParens(right))
    if (leftEs === rightEs) continue
    const es = leftEs ? left : right
    const en = leftEs ? right : left
    const chosen = lang === 'es' ? es : en
    return { text: collapseInnerBilingualParens(chosen, lang), isOriginal: false }
  }
  return { text, isOriginal: true }
}

// ── TASK-WEB-7: safe formatter for Plan QA before/after values ────────────────
//
// The real v3 generator ships issues[].before/after either as plain strings or as
// structured records (dimension records like {width_ft, depth_ft, area_sf,
// was_suspect}). The UI must NEVER interpolate these directly — an object would
// render as "[object Object]" (the Codex blocker). This formatter runs in the data
// layer: strings pass through, objects/arrays flatten into readable bilingual
// key/value fields, and nothing here ever emits "[object Object]" or a $-string
// (the v3 money guard already rejects $ inside the report before render).

/** Bilingual labels for the known dimension-record keys. Unknown keys fall back to
 *  a formatted technical key (underscores/dots → spaces), never dropped. */
const PLAN_QA_FIELD_LABELS: Record<string, BilingualLabel> = {
  width_ft: { en: 'Width (ft)', es: 'Ancho (ft)' },
  // "Profundidad" per Codex polish (TASK-WEB-8) — clearer than "Fondo" for a room depth.
  depth_ft: { en: 'Depth (ft)', es: 'Profundidad (ft)' },
  area_sf: { en: 'Area (sf)', es: 'Área (sf)' },
  was_suspect: { en: 'Flagged as suspect', es: 'Marcado como sospechoso' },
  // The real generator's "after" records use the shorter key `suspect`.
  suspect: { en: 'Flagged as suspect', es: 'Marcado como sospechoso' },
}

export interface PlanQaValueField {
  key: string
  label: BilingualLabel
  value: BilingualLabel
}

export type PlanQaValueView =
  | { kind: 'text'; text: BilingualLabel; original?: boolean }
  | { kind: 'fields'; fields: PlanQaValueField[] }

function prettifyFieldKey(key: string): string {
  return key.replace(/[_.]/g, ' ')
}

function scalarToBilingual(v: unknown): BilingualLabel {
  if (v === null || v === undefined) return { en: '—', es: '—' }
  if (typeof v === 'boolean') return v ? { en: 'Yes', es: 'Sí' } : { en: 'No', es: 'No' }
  const s = String(v)
  return { en: s, es: s }
}

function flattenPlanQaFields(
  node: Record<string, unknown> | unknown[],
  prefix: string,
  out: PlanQaValueField[],
): void {
  const entries: [string, unknown][] = Array.isArray(node)
    ? node.map((v, i) => [String(i + 1), v] as [string, unknown])
    : Object.entries(node)
  for (const [k, v] of entries) {
    const path = prefix ? `${prefix}.${k}` : k
    if (v !== null && typeof v === 'object') {
      flattenPlanQaFields(v as Record<string, unknown> | unknown[], path, out)
    } else {
      const label = PLAN_QA_FIELD_LABELS[path] ?? PLAN_QA_FIELD_LABELS[k]
        ?? { en: prettifyFieldKey(path), es: prettifyFieldKey(path) }
      out.push({ key: path, label, value: scalarToBilingual(v) })
    }
  }
}

/** Derive a render-safe view of a Plan QA before/after value. Returns null when
 *  there is nothing to show (absent/empty) so the row can be skipped entirely. */
export function derivePlanQaValueView(value: unknown): PlanQaValueView | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    if (!value.trim()) return null
    // TASK-WEB-9: string values go through the bilingual splitter — combined
    // bilingual narration shows only the active language; raw single-language
    // evidence keeps its original text and is flagged for the original-text chip.
    const es = splitBilingual(value, 'es')
    const en = splitBilingual(value, 'en')
    return { kind: 'text', text: { en: en.text, es: es.text }, original: es.isOriginal }
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return { kind: 'text', text: scalarToBilingual(value) }
  }
  if (typeof value === 'object') {
    const fields: PlanQaValueField[] = []
    flattenPlanQaFields(value as Record<string, unknown> | unknown[], '', fields)
    return fields.length > 0 ? { kind: 'fields', fields } : null
  }
  return { kind: 'text', text: scalarToBilingual(value) }
}

// Non-blocking polish (TASK-WEB-7): designations_applied arrive as technical
// strings like "owner_designation: …". Render them readably — bilingual label +
// the human part — instead of the raw technical prefix.
const OWNER_DESIGNATION_PREFIX_RX = /^owner_designation:\s*/i

export function formatDesignation(raw: string): { label: BilingualLabel; text: string } {
  if (OWNER_DESIGNATION_PREFIX_RX.test(raw)) {
    return {
      label: { en: 'Owner designation', es: 'Designación del propietario' },
      text: raw.replace(OWNER_DESIGNATION_PREFIX_RX, ''),
    }
  }
  return { label: { en: 'Designation', es: 'Designación' }, text: raw }
}

// Note-borne pending markers (e.g. the siding line's "gabletes pendientes y puertas
// pendientes"). Parsed in the data layer so the mobile pending-first ordering is a
// derived fact, not a CSS guess.
const GABLES_PENDING_RX = /gabletes\s+pendientes|gables[^.]*pending|pending[^.]*gables/i
const DOORS_PENDING_RX = /puertas\s+pendientes|doors[^.]*pending|pending[^.]*doors/i

/** Everything PENDING about a line, as bilingual chips — the pending status (when the
 *  line is not confirmed) plus note-borne pendings (gables/doors). On mobile these
 *  render BEFORE the number (Codex UI correction). */
export function derivePendingHighlights(line: {
  quantification_status: QuantificationStatus
  notes: string
}): BilingualLabel[] {
  const out: BilingualLabel[] = []
  if (isPendingStatus(line.quantification_status)) {
    out.push(FALLBACK_STATUS_LABELS[line.quantification_status])
  }
  if (GABLES_PENDING_RX.test(line.notes)) {
    out.push({ en: 'Gables pending', es: 'Gabletes pendientes' })
  }
  if (DOORS_PENDING_RX.test(line.notes)) {
    out.push({ en: 'Doors pending', es: 'Puertas pendientes' })
  }
  return out
}
